/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useIntl } from 'react-intl'
import SelectableRow from '../../../../client/js/components/selection/SelectableRow'
import CustomerLink from '../customer/CustomerLink'
import FurnishingAndEquipmentLabel from '../labels/FurnishingAndEquipmentLabel'
import Ref from '../Ref'
import EstatePhoto from './EstatePhoto'
import {
  getEstateInterventionListLink,
  getEstateLeaseListLink,
  getEstateLink,
  getEstateMandateListLink,
  getEstatePrintLink,
  getEstateProspectingListLink
} from '../../lib/links/estateLinks'
import { Badge, Menu, Table, Text } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import EstateDescription from '../EstateDescription'
import QuickMenu from '../QuickMenu'
import { Link } from 'react-router-dom'
import Amount from '../transaction/Amount'
import RequirePerm from '../../../auth/components/RequirePerm'

export type EstateListItemProps = {
  imageHeight?: number,
  imageWidth?: number,
  item: Estate
}

function EstateListItem (props: EstateListItemProps) {
  const {
    imageHeight = 90,
    imageWidth = 160,
    item
  } = props
  const intl = useIntl()
  return (
    <SelectableRow value={item.id}>
      <Table.Td>
        <Badge color={item.published ? 'green' : 'gray'}>
          {item.published
            ? intl.formatMessage({
              id: 'published',
              defaultMessage: 'Publié'
            })
            : intl.formatMessage({
              id: 'not_published',
              defaultMessage: 'Non publié'
            })}
        </Badge>
      </Table.Td>
      <Table.Td>
        <EstatePhoto
          photos={item.Photos}
          height={imageHeight}
          width={imageWidth}
        />
      </Table.Td>
      <Table.Td>
        <Ref value={item.reference} />
      </Table.Td>
      <Table.Td>
        <AnchorLink to={getEstateLink(item.id)}>
          {item.title}
        </AnchorLink>
        <Text size="xs">
          <EstateDescription
            transaction={item.transaction}
            type={item.type}
          />
        </Text>
        <Text size="xs">
          <FurnishingAndEquipmentLabel
            equipped={item.equipped}
            furnished={item.furnished}
          />
        </Text>
      </Table.Td>
      <Table.Td>
        {item.City?.name}
        {item.residence != null
          ? <Text size="xs">
            {item.residence}
          </Text>
          : null}
      </Table.Td>
      <Table.Td ta="end">
        <Amount value={item.price} inherit withCurrency />
      </Table.Td>
      <Table.Td>
        {item.Owner
          ? <CustomerLink customer={item.Owner} />
          : null}
      </Table.Td>
      <Table.Td>
        <QuickMenu buttonProps={{ size: 'xs' }}>
          <RequirePerm
            permissions={['read:lease']}
            render={() => (
              <Menu.Item component={Link} to={getEstateLeaseListLink(item.id)}>
                {intl.formatMessage({
                  id: 'leases',
                  defaultMessage: 'Dossiers de location'
                })}
              </Menu.Item>
            )}
          />
          <RequirePerm
            permissions={['read:intervention']}
            render={() => (
              <Menu.Item component={Link} to={getEstateInterventionListLink(item.id)}>
                {intl.formatMessage({
                  id: 'interventions',
                  defaultMessage: 'Interventions'
                })}
              </Menu.Item>
            )}
          />
          <RequirePerm
            permissions={['read:mandate']}
            render={() => (
              <Menu.Item component={Link} to={getEstateMandateListLink(item.id)}>
                {intl.formatMessage({
                  id: 'mandates',
                  defaultMessage: 'Mandats'
                })}
              </Menu.Item>
            )}
          />
          <RequirePerm
            permissions={['read:prospecting']}
            render={() => (
              <Menu.Item component={Link} to={getEstateProspectingListLink(item.id)}>
                {intl.formatMessage({
                  id: 'prospecting_list',
                  defaultMessage: 'Prospections'
                })}
              </Menu.Item>
            )}
          />
          <Menu.Divider />
          <Menu.Item
            component={Link}
            to={getEstatePrintLink(item.id)}
            rel="noopener noreferrer"
            target="_blank"
          >
            {intl.formatMessage({
              id: 'print',
              defaultMessage: 'Imprimer'
            })}
          </Menu.Item>
        </QuickMenu>
      </Table.Td>
    </SelectableRow>
  )
}

export default EstateListItem
